import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { GLASS_POPOVER } from './glass';

const GAP = 8;
const VIEWPORT_MARGIN = 12;
const FOCUSABLE = 'input, select, textarea, button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/**
 * Glass popover anchored to a trigger. It renders in a portal, so scroll
 * containers (e.g. a table's) can't clip it, and follows the trigger while
 * anything scrolls. Closes on a press outside and on Escape; Escape is
 * handled in the capture phase and stopped there, so neither page-level
 * Escape shortcuts nor a dialog around the popover react to it. Sits above
 * dialogs (Modal is z-1100), so it also works inside one.
 */
const Popover = ({ anchorRef, open, onClose, align = 'start', label, role = 'dialog', className = '', children }) => {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const rect = anchor.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    const { offsetWidth: width, offsetHeight: height } = panel;

    const preferredLeft = align === 'end' ? rect.right - width : rect.left;
    const left = Math.min(Math.max(VIEWPORT_MARGIN, preferredLeft), viewportWidth - width - VIEWPORT_MARGIN);
    let top = rect.bottom + GAP;
    // Flip above the trigger when there is no room below
    if (top + height > viewportHeight - VIEWPORT_MARGIN && rect.top - GAP - height > VIEWPORT_MARGIN) {
      top = rect.top - GAP - height;
    }
    setPosition((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, [anchorRef, align]);

  // Placed before paint; the last position is kept so the exit animation
  // plays where the panel was.
  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    const observer = new ResizeObserver(updatePosition);
    if (panelRef.current) observer.observe(panelRef.current);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      observer.disconnect();
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (panelRef.current?.contains(event.target) || anchorRef.current?.contains(event.target)) return;
      onCloseRef.current();
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      // Only the popover closes, not a dialog it was opened from
      event.stopPropagation();
      onCloseRef.current();
      anchorRef.current?.focus();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open, anchorRef]);

  // Move focus into the panel (its first field or button) once it is placed
  useEffect(() => {
    if (open && position) {
      const target = panelRef.current?.querySelector('[data-autofocus]') || panelRef.current?.querySelector(FOCUSABLE);
      if (target && !panelRef.current.contains(document.activeElement)) target.focus({ preventScroll: true });
    }
  }, [open, position]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.97 }}
          transition={{ duration: 0.15 }}
          style={{
            top: position ? position.top : 0,
            left: position ? position.left : 0,
            visibility: position ? 'visible' : 'hidden',
          }}
          className="tw-fixed tw-z-[1150] tw-font-sans tw-antialiased"
        >
          <div
            ref={panelRef}
            role={role}
            aria-label={label}
            className={`tw-max-w-[calc(100vw-1.5rem)] tw-rounded-2xl tw-text-slate-700 dark:tw-text-slate-200 ${GLASS_POPOVER} ${className}`}
          >
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

Popover.propTypes = {
  anchorRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  align: PropTypes.oneOf(['start', 'end']),
  label: PropTypes.string,
  role: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Popover;
